import React from 'react';
import { Shield } from 'lucide-react';
import { LegalPage } from './LegalPage';

interface Props {
  onBack: () => void;
  onNavigatePrivacy: () => void;
  onNavigateTerms: () => void;
}

export const PrivacyPolicy: React.FC<Props> = ({ onBack, onNavigatePrivacy, onNavigateTerms }) => {
  return (
      <LegalPage 
        title="Privacy Policy" 
        lastUpdated="January 12, 2026" /* @live-data-exempt: legal-effective-date */ 
        icon={<Shield size={32} strokeWidth={1.5} />}
        onBack={onBack}
        onNavigatePrivacy={onNavigatePrivacy}
      onNavigateTerms={onNavigateTerms}
    >
      <p>
        Intellio Labs, Inc. (<strong>"Intellio," "we," "us," or "our"</strong>) is committed to protecting the privacy and security of the personal information we collect through our platform and services (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
      </p>
      <br /><br />
      <p>
        By using the Service, you consent to the data practices described in this Privacy Policy. If you do not agree with this Privacy Policy, please do not access or use the Service.
      </p>
      <br /><br />

      <h2>1. INFORMATION WE COLLECT</h2>
      <br /><br />
      <p>We collect several types of information from and about users of our Service:</p>
      <br /><br />

      <h3>1.1 Personal Information</h3>
      <p>Personal information is data that can be used to identify you or your child. We may collect:</p>
      <ul>
        <li>Name, email address, phone number, and mailing address</li>
        <li>Date of birth and age</li>
        <li>Student identification numbers</li>
        <li>Parent/guardian contact information</li>
        <li>Demographic information (gender, grade level, etc.)</li>
        <li>Emergency contact information</li>
        <li>Photographs and video (when provided by schools)</li>
        <li>Academic records and performance data</li>
        <li>Attendance records</li>
        <li>Behavioral and disciplinary records</li>
        <li>Health information (as needed for school operations, such as allergies or medical conditions)</li>
        <li>Financial information related to tuition, financial aid, and school payments</li>
        <li>Communication preferences</li>
      </ul>
      <br /><br />

      <h3>1.2 Educational Records</h3>
      <p>For students, we may collect and process educational records, which may include:</p>
      <ul>
        <li>Enrollment and registration information</li>
        <li>Course schedules and academic transcripts</li>
        <li>Standardized test scores</li>
        <li>Special education records and <strong>Individualized Education Programs (IEPs)</strong></li>
        <li>Extracurricular activity participation</li>
        <li>Teacher assessments and progress reports</li>
      </ul>
      <br /><br />

      <h3>1.3 Usage Data</h3>
      <p>We automatically collect certain information when you access and use the Service:</p>
      <ul>
        <li>Device information (device type, operating system, browser type)</li>
        <li>IP address and general location information</li>
        <li>Log data (access times, pages viewed, actions taken)</li>
        <li>Cookies and similar tracking technologies</li>
        <li>Performance and diagnostic data</li>
      </ul>
      <br /><br />

      <h3>1.4 Communications</h3>
      <p>We collect information you provide when you:</p>
      <ul>
        <li>Contact our support team</li>
        <li>Participate in surveys or feedback forms</li>
        <li>Communicate through the Service's messaging features</li>
        <li>Submit inquiries or requests</li>
      </ul>
      <br /><br />

      <h2>2. HOW WE USE YOUR INFORMATION</h2>
      <br /><br />
      <p>We use the information we collect to:</p>
      <br /><br />

      <h3>2.1 Provide and Maintain the Service</h3>
      <ul>
        <li>Create and manage user accounts</li>
        <li>Process admissions applications and enrollment</li>
        <li>Facilitate communication between schools, parents, teachers, and students</li>
        <li>Manage financial aid applications and processing</li>
        <li>Track student attendance and academic progress</li>
        <li>Generate reports and analytics for school administrators</li>
        <li>Provide technical support and respond to inquiries</li>
      </ul>
      <br /><br />

      <h3>2.2 Improve and Develop the Service</h3>
      <ul>
        <li>Analyze usage patterns to enhance user experience</li>
        <li>Develop new features and functionality</li>
        <li>Train and improve our <strong>AI and machine learning models</strong></li>
        <li>Conduct research and analytics to improve our Service</li>
        <li>Test new features and optimize performance</li>
      </ul>
      <br /><br />

      <h3>2.3 Communicate with You</h3>
      <ul>
        <li>Send administrative information, updates, and notifications</li>
        <li>Respond to your inquiries and support requests</li>
        <li>Send marketing communications (where you have provided consent)</li>
        <li>Provide important notices about changes to our Service or policies</li>
      </ul>
      <br /><br />

      <h3>2.4 Ensure Security and Compliance</h3>
      <ul>
        <li>Detect, prevent, and address technical issues and security threats</li>
        <li>Enforce our Terms and Conditions and other policies</li>
        <li>Comply with legal obligations and regulatory requirements</li>
        <li>Protect the rights, property, and safety of Intellio, our users, and others</li>
      </ul>
      <br /><br />

      <h2>3. STUDENT DATA PROTECTION</h2>
      <br /><br />

      <h3>3.1 FERPA Compliance</h3>
      <p>We comply with the <strong>Family Educational Rights and Privacy Act (FERPA)</strong>, which protects the privacy of student education records. We act as a school official with legitimate educational interests when processing student data on behalf of educational institutions.</p>
      <br /><br />

      <h3>3.2 COPPA Compliance</h3>
      <p>For children under 13, we comply with the <strong>Children's Online Privacy Protection Act (COPPA)</strong>. We collect, use, and disclose children's personal information only with verifiable parental consent obtained by the educational institution or as otherwise permitted by COPPA.</p>
      <br /><br />

      <h3>3.3 State Privacy Laws</h3>
      <p>We comply with applicable state student privacy laws, including but not limited to:</p>
      <ul>
        <li>California AB 1584 (Student Online Personal Information Protection Act - SOPIPA)</li>
        <li>New York Education Law Section 2-d</li>
        <li>Other state-specific student data privacy requirements</li>
      </ul>
      <br /><br />

      <h3>3.4 Limited Use of Student Data</h3>
      <p>We do <strong>not</strong>:</p>
      <ul>
        <li>Sell or rent student personal information</li>
        <li>Use student data to target advertisements</li>
        <li>Build personal profiles of students beyond what is necessary for educational purposes</li>
        <li>Disclose student information to third parties except as required by law or with proper authorization</li>
      </ul>
      <br /><br />

      <h2>4. HOW WE SHARE YOUR INFORMATION</h2>
      <br /><br />

      <h3>4.1 With Educational Institutions</h3>
      <p>We share information with the educational institution(s) with which you are affiliated for educational and administrative purposes.</p>
      <br /><br />

      <h3>4.2 With Service Providers</h3>
      <p>We may share information with third-party service providers who perform services on our behalf, including:</p>
      <ul>
        <li>Cloud hosting and infrastructure providers</li>
        <li>Payment processing services</li>
        <li>Customer support platforms</li>
        <li>Analytics and data analysis services</li>
        <li>Email delivery services</li>
      </ul>
      <br /><br />
      <p>All service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>
      <br /><br />

      <h3>4.3 For Legal Reasons</h3>
      <p>We may disclose information when required by law or when we believe disclosure is necessary to:</p>
      <ul>
        <li>Comply with legal obligations, court orders, or legal process</li>
        <li>Enforce our Terms and Conditions</li>
        <li>Protect the rights, property, and safety of Intellio, our users, or others</li>
        <li>Prevent fraud or security threats</li>
      </ul>
      <br /><br />

      <h3>4.4 With Your Consent</h3>
      <p>We may share information with third parties when you provide explicit consent.</p>
      <br /><br />

      <h3>4.5 Business Transfers</h3>
      <p>In connection with a merger, acquisition, reorganization, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.</p>
      <br /><br />

      <h3>4.6 Aggregated or De-Identified Data</h3>
      <p>We may share aggregated or de-identified information that cannot reasonably be used to identify you or your child for research, analytics, or marketing purposes.</p>
      <br /><br />

      <h2>5. DATA SECURITY</h2>
      <br /><br />

      <h3>5.1 Security Measures</h3>
      <p>We implement industry-standard technical, administrative, and physical security measures to protect your information, including:</p>
      <ul>
        <li>Encryption of data in transit and at rest</li>
        <li>Secure authentication and access controls</li>
        <li>Regular security assessments and audits</li>
        <li>Employee training on data privacy and security</li>
        <li>Network security and monitoring systems</li>
        <li>Incident response procedures</li>
      </ul>
      <br /><br />

      <h3>5.2 Limitations</h3>
      <p>While we take reasonable precautions to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information.</p>
      <br /><br />

      <h3>5.3 Breach Notification</h3>
      <p>In the event of a data breach affecting personal information, we will notify affected users and relevant authorities as required by applicable law.</p>
      <br /><br />

      <h2>6. DATA RETENTION</h2>
      <p>We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
      <br /><br />

      <h3>6.1 Student Records</h3>
      <p>Student education records are retained in accordance with FERPA, state law requirements, and the educational institution's record retention policies, typically for the duration of the student's enrollment plus a specified period thereafter.</p>
      <br /><br />

      <h3>6.2 Account Information</h3>
      <p>Account information is retained for as long as your account is active or as needed to provide the Service.</p>
      <br /><br />

      <h3>6.3 Deletion Requests</h3>
      <p>Upon request or account termination, we will delete or anonymize personal information, subject to:</p>
      <ul>
        <li>Legal retention obligations</li>
        <li>Legitimate business purposes (e.g., dispute resolution, fraud prevention)</li>
        <li>Technical limitations</li>
        <li>Backup systems (which are periodically purged)</li>
      </ul>
      <br /><br />

      <h2>7. YOUR RIGHTS AND CHOICES</h2>
      <br /><br />

      <h3>7.1 Access and Correction</h3>
      <p>You have the right to access and correct your personal information. You may update your account information through the Service or by contacting us.</p>
      <br /><br />

      <h3>7.2 FERPA Rights</h3>
      <p>Parents and eligible students (18 years or older) have rights under FERPA to:</p>
      <ul>
        <li>Inspect and review education records</li>
        <li>Request correction of inaccurate or misleading information</li>
        <li>Consent to disclosure of personally identifiable information (with certain exceptions)</li>
        <li>File complaints with the U.S. Department of Education</li>
      </ul>
      <br /><br />

      <h3>7.3 Opt-Out Rights</h3>
      <p>You may opt out of:</p>
      <ul>
        <li>Marketing communications (via unsubscribe links or account settings)</li>
        <li>Certain cookies and tracking technologies (via browser settings)</li>
        <li>Non-essential data collection (where technically feasible)</li>
      </ul>
      <br /><br />

      <h3>7.4 Data Portability</h3>
      <p>Upon request, we will provide you with a copy of your personal information in a structured, commonly used format.</p>
      <br /><br />

      <h3>7.5 Deletion</h3>
      <p>You may request deletion of your personal information, subject to legal and operational limitations. Educational institutions are responsible for managing student records deletion requests.</p>
      <br /><br />

      <h3>7.6 Additional State-Specific Rights</h3>
      <p>If you reside in certain states (e.g., California, Virginia, Colorado), you may have additional privacy rights, including:</p>
      <ul>
        <li>Right to know what personal information we collect</li>
        <li>Right to delete personal information</li>
        <li>Right to opt out of sale of personal information (we do not sell personal information)</li>
        <li>Right to non-discrimination for exercising privacy rights</li>
      </ul>
      <br /><br />

      <h2>8. CHILDREN'S PRIVACY</h2>
      <br /><br />

      <h3>8.1 Age Restrictions</h3>
      <p>The Service is not intended for use by children under 13 without the involvement of a parent, guardian, or educational institution. We do not knowingly collect personal information from children under 13 except in the educational context with school or parental authorization.</p>
      <br /><br />

      <h3>8.2 Parental Rights</h3>
      <p>Parents have the right to:</p>
      <ul>
        <li>Review their child's personal information</li>
        <li>Request deletion of their child's personal information</li>
        <li>Refuse further collection or use of their child's information</li>
        <li>Contact us with questions or concerns</li>
      </ul>
      <br /><br />

      <h3>8.3 School Authorization</h3>
      <p>When we collect information from students under 13 in the school context, we rely on the school's consent on behalf of parents as permitted by COPPA.</p>
      <br /><br />

      <h2>9. COOKIES AND TRACKING TECHNOLOGIES</h2>
      <br /><br />

      <h3>9.1 Types of Cookies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Maintain user sessions and authentication</li>
        <li>Remember user preferences and settings</li>
        <li>Analyze usage patterns and improve the Service</li>
        <li>Provide security features</li>
      </ul>
      <br /><br />

      <h3>9.2 Cookies We Use</h3>
      <ul>
        <li><strong>Strictly necessary cookies</strong> (required for Service functionality)</li>
        <li><strong>Performance cookies</strong> (analytics and usage tracking)</li>
        <li><strong>Functional cookies</strong> (user preferences and settings)</li>
        <li><strong>Targeting cookies</strong> (limited use with consent)</li>
      </ul>
      <br /><br />

      <h3>9.3 Cookie Management</h3>
      <p>You can control cookies through your browser settings. Disabling certain cookies may limit Service functionality.</p>
      <br /><br />

      <h3>9.4 Do Not Track</h3>
      <p>We currently do not respond to Do Not Track signals, but we do not track users across third-party websites.</p>
      <br /><br />

      <h2>10. THIRD-PARTY LINKS AND SERVICES</h2>
      <p>The Service may contain links to third-party websites, applications, or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access through our Service.</p>
      <br /><br />

      <h2>11. ARTIFICIAL INTELLIGENCE AND MACHINE LEARNING</h2>
      <br /><br />

      <h3>11.1 AI Data Usage</h3>
      <p>We use AI and machine learning technologies to improve the Service, automate administrative tasks, and provide intelligent features. This may involve processing personal information to:</p>
      <ul>
        <li>Train and improve AI models</li>
        <li>Generate automated recommendations</li>
        <li>Detect patterns and anomalies</li>
        <li>Provide personalized experiences</li>
      </ul>
      <br /><br />

      <h3>11.2 Human Oversight</h3>
      <p>AI-generated outputs are subject to human review and approval, particularly for decisions affecting students, admissions, or financial aid.</p>
      <br /><br />

      <h3>11.3 Data Minimization</h3>
      <p>We use the minimum amount of data necessary to train and operate our AI systems and employ techniques such as anonymization and aggregation where possible.</p>
      <br /><br />

      <h2>12. INTERNATIONAL DATA TRANSFERS</h2>
      <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable law.</p>
      <br /><br />

      <h2>13. CHANGES TO THIS PRIVACY POLICY</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
      <ul>
        <li>Posting the updated Privacy Policy on the Service</li>
        <li>Updating the "Last Updated" date</li>
        <li>Sending email notification (for significant changes)</li>
      </ul>
      <p>Your continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy.</p>
      <br /><br />

      <h2>14. CALIFORNIA PRIVACY RIGHTS</h2>
      <p>If you are a California resident, you have specific rights under the <strong>California Consumer Privacy Act (CCPA)</strong> and <strong>California Privacy Rights Act (CPRA)</strong>:</p>
      <br /><br />

      <h3>14.1 Right to Know</h3>
      <p>You have the right to request information about the categories and specific pieces of personal information we have collected about you.</p>
      <br /><br />

      <h3>14.2 Right to Delete</h3>
      <p>You have the right to request deletion of your personal information, subject to certain exceptions.</p>
      <br /><br />

      <h3>14.3 Right to Correct</h3>
      <p>You have the right to request correction of inaccurate personal information.</p>
      <br /><br />

      <h3>14.4 Right to Opt-Out</h3>
      <p>You have the right to opt out of the sale or sharing of your personal information. <strong>We do not sell or share personal information.</strong></p>
      <br /><br />

      <h3>14.5 Right to Limit Use of Sensitive Personal Information</h3>
      <p>You have the right to limit our use of sensitive personal information to what is necessary to provide the Service.</p>
      <br /><br />

      <h3>14.6 Non-Discrimination</h3>
      <p>We will not discriminate against you for exercising your CCPA/CPRA rights.</p>
      <br /><br />

      <h2>15. EUROPEAN UNION PRIVACY RIGHTS</h2>
      <p>If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have rights under the <strong>General Data Protection Regulation (GDPR)</strong>:</p>
      <br /><br />

      <h3>15.1 Legal Basis for Processing</h3>
      <p>We process personal data based on:</p>
      <ul>
        <li>Contractual necessity (to provide the Service)</li>
        <li>Legitimate interests (to improve and secure the Service)</li>
        <li>Legal obligations (compliance with applicable laws)</li>
        <li>Consent (where required)</li>
      </ul>
      <br /><br />

      <h3>15.2 Your Rights</h3>
      <ul>
        <li>Right to access your personal data</li>
        <li>Right to rectification of inaccurate data</li>
        <li>Right to erasure ("right to be forgotten")</li>
        <li>Right to restrict processing</li>
        <li>Right to data portability</li>
        <li>Right to object to processing</li>
        <li>Right to withdraw consent</li>
        <li>Right to lodge a complaint with a supervisory authority</li>
      </ul>
      <br /><br />

      <h2>16. CONTACT US</h2>
      <p>If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
      <p>
        <strong>Intellio Labs, Inc.</strong><br />
        Privacy Officer<br />
        Email: <a href="mailto:privacy@intellio.com">privacy@intellio.com</a><br />
        Address: [Your Business Address]<br />
        Phone: [Your Phone Number]
      </p>
      <br /><br />
      <p>
        For FERPA-related inquiries, educational institutions should contact their designated school official. Parents and eligible students may contact us or their school administrator.
      </p>
      <br /><br />
      <p>
        For data protection inquiries from the EU/EEA:<br />
        Data Protection Officer: <a href="mailto:dpo@intellio.com">dpo@intellio.com</a>
      </p>
      <br /><br />

      <h2>17. COMPLAINTS AND REGULATORY AUTHORITIES</h2>
      <p>If you believe we have not addressed your privacy concerns adequately, you have the right to file a complaint with:</p>
      <ul>
        <li>U.S. Department of Education (for FERPA complaints)</li>
        <li>Federal Trade Commission (for COPPA complaints)</li>
        <li>Your state's Attorney General or consumer protection office</li>
        <li>Your local data protection authority (for EU/EEA residents)</li>
      </ul>
      <br /><br />

      <h2>18. ACKNOWLEDGMENT</h2>
      <p className="font-bold uppercase">
        BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY AND AGREE TO ITS TERMS.
      </p>
    </LegalPage>
  );
};
